import { LogOutIcon, SettingsIcon, UserCircleIcon } from 'lucide-react';
import React from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { Button, Form, Input, Modal, message } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutRequest } from '../../redux/auth/action';
import EmployeeResponse from '../../model/Employee.model';
import EmployeeService from '../../service/employee-service/Employee.service';
import NotificationBell from '../Noti/NotificationBell';
import { changePassword } from '../../service/auth-service/auth-services';

const Header: React.FC = () => {
  const [employee, setEmployee] = React.useState<EmployeeResponse>();
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
  const [changingPassword, setChangingPassword] = React.useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleLogout = () => {
    dispatch(logoutRequest({}));
    navigate('/login');
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchEmployee = async () => {
    try {
      const findEmployee = await EmployeeService.getEmployeeByUserId(user.id);
      setEmployee(findEmployee);
    } catch (error) {
      console.error('Error fetching employee:', error);
    }
  };

  React.useEffect(() => {
    fetchEmployee();
  }, []);

  return (
    <header className="bg-white text-gray-600 shadow-md border-b border-gray-200 ">
      <div className="container mx-auto px-4 py-3 flex items-center justify-end">
        <nav className="flex items-center gap-2">
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-gray-300 transition-colors ml-auto">
                <UserCircleIcon size={24} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-50 bg-white border border-gray-200 rounded-md shadow-lg p-4">
              <div className="flex flex-col mb-2">
                <span className="font-semibold">{employee?.name}</span>
                <span className="text-sm text-gray-500">{user.username}</span>
              </div>

              <DropdownMenuItem
                onClick={() => {
                  form.resetFields();
                  setChangePasswordOpen(true);
                }}
                className="flex items-center space-x-2 text-gray-700 hover:bg-gray-100 transition-colors p-2 rounded-md cursor-pointer"
              >
                <SettingsIcon size={20} />
                <span>Đổi mật khẩu</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center space-x-2 text-red-600 hover:bg-red-100 transition-colors p-2 rounded-md cursor-pointer"
              >
                <LogOutIcon size={20} />
                <span>Đăng xuất</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>

      <Modal
        title="Đổi mật khẩu"
        open={changePasswordOpen}
        onCancel={() => setChangePasswordOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              setChangingPassword(true);
              await changePassword({
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
                confirmNewPassword: values.confirmNewPassword,
              });
              message.success('Đổi mật khẩu thành công');
              setChangePasswordOpen(false);
            } catch (e: any) {
              message.error(e?.message || 'Đổi mật khẩu thất bại');
            } finally {
              setChangingPassword(false);
            }
          }}
        >
          <Form.Item
            name="oldPassword"
            label="Mật khẩu cũ"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu cũ' }]}
          >
            <Input.Password placeholder="Nhập mật khẩu cũ" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="Mật khẩu mới"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu mới' }]}
          >
            <Input.Password placeholder="Nhập mật khẩu mới" />
          </Form.Item>

          <Form.Item
            name="confirmNewPassword"
            label="Xác nhận mật khẩu mới"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Vui lòng xác nhận mật khẩu mới' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Xác nhận mật khẩu mới không khớp')
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Nhập lại mật khẩu mới" />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setChangePasswordOpen(false)}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit" loading={changingPassword}>
              Đổi mật khẩu
            </Button>
          </div>
        </Form>
      </Modal>
    </header>
  );
};

export default Header;
